import React from "react";
import { ArrowUpRight, Star } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1749544812189-193ccae5e2f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Premium gold and obsidian texture backdrop"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]"></div>
        <div className="absolute inset-0 grain"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24 pt-40 md:pt-48 pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8" data-testid="hero-eyebrow">
              <span className="h-px w-10 bg-gold"></span>
              <span className="text-[11px] uppercase tracking-[0.32em] text-white/60">
                Manchester · Established 2025
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-white text-5xl sm:text-6xl lg:text-[7.5rem] leading-[0.92] tracking-tighter"
            >
              Premium <em className="not-italic gold-text-gradient">Digital</em>
              <br />
              Solutions for
              <br />
              Manchester
              <br />
              <span className="italic font-medium text-white/85">Businesses.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-10 max-w-2xl text-base md:text-lg text-white/65 leading-relaxed"
            >
              We craft bespoke websites, powerful applications and strategic branding
              that help ambitious local businesses dominate online — from the Northern
              Quarter to the leafy suburbs of Cheshire.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4" data-testid="hero-ctas">
              <a
                href="#contact"
                data-testid="cta-consultation"
                className="shine inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-semibold gold-bg-gradient text-black hover:opacity-90 transition-opacity"
              >
                Get Free Quote
                <ArrowUpRight size={18} />
              </a>
              <a
                href="#portfolio"
                data-testid="cta-view-work"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-semibold border border-white/25 text-white hover:bg-white/5 hover:border-white/40 transition-all backdrop-blur-md"
              >
                View Our Work
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 lg:pl-8" data-testid="hero-side-card">
            <div className="glass-strong rounded-sm p-8 relative">
              <div className="flex items-center gap-1 text-gold mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" />
                ))}
                <span className="ml-2 text-xs uppercase tracking-[0.2em] text-white/60">
                  4.9 / 5
                </span>
              </div>
              <p className="font-display italic text-white/90 leading-relaxed text-lg">
                "Acnira built us a website worthy of a Michelin nod — reservations
                doubled in the first month."
              </p>
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full gold-bg-gradient flex items-center justify-center text-black font-bold">
                  OH
                </div>
                <div>
                  <div className="text-sm text-white/90">Olivia Harrington</div>
                  <div className="text-xs text-white/50">Founder · Salt & Ember</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="glass p-5">
                <div className="font-display text-3xl text-white">120+</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-1">
                  Brands Launched
                </div>
              </div>
              <div className="glass p-5 relative overflow-hidden">
                <div className="font-display text-3xl gold-text-gradient">20%</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-1">
                  Referral Reward
                </div>
                <div className="absolute -bottom-12 -right-12 h-28 w-28 rounded-full bg-gold/10 blur-3xl"></div>
              </div>
            </div>
            <div className="mt-3 glass p-4 flex items-center gap-3">
              <span className="h-2 w-2 rounded-full bg-gold animate-pulse"></span>
              <p className="text-sm text-white/75">
                <span className="text-white font-medium">Get 20%</span> when you get us a client.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative z-10 border-y border-white/10 py-6 overflow-hidden">
        <div className="marquee flex whitespace-nowrap items-center gap-16 text-white/30 font-display italic text-2xl md:text-3xl">
          {Array.from({ length: 2 }).map((_, idx) => (
            <React.Fragment key={idx}>
              <span>Restaurants</span>
              <span className="text-gold">✦</span>
              <span>Estate Agents</span>
              <span className="text-gold">✦</span>
              <span>Salons & Spas</span>
              <span className="text-gold">✦</span>
              <span>Boutique Retail</span>
              <span className="text-gold">✦</span>
              <span>Premium Gyms</span>
              <span className="text-gold">✦</span>
              <span>Master Tradesmen</span>
              <span className="text-gold">✦</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
